import React, { useState, useEffect, useCallback } from 'react';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  BuildingOfficeIcon,
  CheckCircleIcon,
  XCircleIcon
} from '@heroicons/react/24/outline';
import { Company, CompanyCreate, CompanyUpdate, companiesService } from '../services/companies';
import { useTranslation } from 'react-i18next';
import { formatDate } from '../utils/i18nFormat';

interface CompanyFormData {
  name: string;
  domain: string;
  is_active: boolean;
}

const Companies: React.FC = () => {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editingCompany, setEditingCompany] = useState<Company | null>(null);
  const [formData, setFormData] = useState<CompanyFormData>({
    name: '',
    domain: '',
    is_active: true
  });
  const [submitting, setSubmitting] = useState(false);
  const { t } = useTranslation();

  const loadCompanies = useCallback(async () => {
    try {
      setLoading(true);
      const data = await companiesService.getAll();
      setCompanies(data);
      setError(null);
    } catch (err: any) {
      setError(t('Error loading companies'));
      console.error('Erro ao carregar empresas:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCompanies();
  }, [loadCompanies]);

  const handleOpenModal = (company?: Company) => {
    if (company) {
      setEditingCompany(company);
      setFormData({
        name: company.name,
        domain: company.domain,
        is_active: company.is_active
      });
    } else {
      setEditingCompany(null);
      setFormData({
        name: '',
        domain: '',
        is_active: true
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingCompany(null);
    setFormData({
      name: '',
      domain: '',
      is_active: true
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      if (editingCompany) {
        await companiesService.update(editingCompany.id, formData);
      } else {
        await companiesService.create(formData);
      }

      await loadCompanies();
      handleCloseModal();
      setError(null);
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || t('companies.error.saving');
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (company: Company) => {
    if (!window.confirm(t('companies.confirmDelete', { name: company.name }))) {
      return;
    }

    try {
      await companiesService.delete(company.id);
      await loadCompanies();
      setError(null);
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || t('companies.error.deleting');
      setError(errorMessage);
    }
  };

  if (loading) {
    return (
      <div className='space-y-6'>
        <div>
          <h1 className='text-2xl font-bold text-gray-900 dark:text-gray-100'>{t('Companies')}</h1>
          <p className='mt-1 text-sm text-gray-500'>{t('companies.subtitle')}</p>
        </div>
        <div className='card dark:bg-gray-800'>
          <div className='animate-pulse'>
            <div className='h-4 bg-gray-200 rounded w-1/4 mb-4'></div>
            <div className='space-y-3'>
              <div className='h-4 bg-gray-200 rounded'></div>
              <div className='h-4 bg-gray-200 rounded w-5/6'></div>
              <div className='h-4 bg-gray-200 rounded w-4/6'></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      <div className='flex justify-between items-center'>
        <div>
          <h1 className='text-2xl font-bold text-gray-900 dark:text-white'>{t('Companies')}</h1>
          <p className='mt-1 text-sm text-gray-500'>{t('companies.subtitle')}</p>
        </div>
        <button onClick={() => handleOpenModal()} className='btn btn-primary flex items-center gap-2'>
          <PlusIcon className='h-5 w-5' />
            {t('companies.new')}
        </button>
      </div>

      {error && (
        <div className='bg-red-50 border border-red-200 rounded-md p-4'>
          <div className='flex'>
            <XCircleIcon className='h-5 w-5 text-red-400' />
            <div className='ml-3'>
              <p className='text-sm text-red-800'>{error}</p>
            </div>
          </div>
        </div>
      )}

      <div className='card dark:bg-gray-800'>
        {companies.length === 0 ? (
          <div className='text-center py-12'>
            <BuildingOfficeIcon className='mx-auto h-12 w-12 text-gray-400' />
            <h3 className='mt-2 text-sm font-medium text-gray-900 dark:text-gray-300'>{t('companies.none')}</h3>
            <p className='mt-1 text-sm text-gray-500'>{t('companies.emptyDescription')}</p>
            <div className='mt-6'>
              <button onClick={() => handleOpenModal()} className='btn btn-primary flex items-center gap-2 mx-auto'>
                <PlusIcon className='h-5 w-5' />
                  {t('companies.new')}
              </button>
            </div>
          </div>
        ) : (
          <div className='overflow-hidden'>
            <table className='min-w-full divide-y divide-gray-200'>
              <thead className='bg-gray-50 dark:bg-gray-700'>
                <tr>
                  <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                      {t('Company')}
                  </th>
                  <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                      {t('Domain')}
                  </th>
                  <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                      {t('Status')}
                  </th>
                  <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                      {t('Created at')}
                  </th>
                  <th className='px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider'>
                      {t('Actions')}
                  </th>
                </tr>
              </thead>
              <tbody className='bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700'>
                {companies.map(company => (
                  <tr key={company.id} className='hover:bg-gray-50'>
                    <td className='px-6 py-4 whitespace-nowrap'>
                      <div className='flex items-center'>
                        <BuildingOfficeIcon className='h-5 w-5 text-gray-400 mr-3' />
                        <div>
                          <div className='text-sm font-medium text-gray-900 dark:text-white'>{company.name}</div>
                        </div>
                      </div>
                    </td>
                    <td className='px-6 py-4 whitespace-nowrap'>
                      <div className='text-sm text-gray-900 dark:text-gray-300'>{company.domain}</div>
                    </td>
                    <td className='px-6 py-4 whitespace-nowrap'>
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          company.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {company.is_active ? (
                          <>
                            <CheckCircleIcon className='h-3 w-3 mr-1' />
                              {t('Active')}
                          </>
                        ) : (
                          <>
                            <XCircleIcon className='h-3 w-3 mr-1' />
                              {t('Inactive')}
                          </>
                        )}
                      </span>
                    </td>
                    <td className='px-6 py-4 whitespace-nowrap text-sm text-gray-500'>
                      {formatDate(company.created_at)}
                    </td>
                    <td className='px-6 py-4 whitespace-nowrap text-right text-sm font-medium'>
                      <div className='flex justify-end gap-2'>
                        <button
                          onClick={() => handleOpenModal(company)}
                          className='text-indigo-600 hover:text-indigo-900 p-1 rounded'
                          title={t('Edit')}
                        >
                          <PencilIcon className='h-4 w-4' />
                        </button>
                        <button
                          onClick={() => handleDelete(company)}
                          className='text-red-600 hover:text-red-900 p-1 rounded'
                          title={t('Delete')}
                        >
                          <TrashIcon className='h-4 w-4' />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className='fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50'>
          <div className='relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white dark:bg-gray-800 dark:border-gray-700'>
            <div className='mt-3'>
              <h3 className='text-lg font-medium text-gray-900 dark:text-white mb-4'>
                {editingCompany ? t('companies.modal.editTitle') : t('companies.modal.createTitle')}
              </h3>

              <form onSubmit={handleSubmit} className='space-y-4'>
                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-1'>{t('companies.form.companyName')}</label>
                  <input
                    type='text'
                    value={formData.name}
                    onChange={e => setFormData({ ...formData, name: e.target.value })}
                    className='w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500'
                    placeholder={t('companies.form.companyNamePlaceholder')}
                    required
                  />
                </div>

                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-1'>{t('companies.form.primaryDomain')}</label>
                  <input
                    type='text'
                    value={formData.domain}
                    onChange={e => setFormData({ ...formData, domain: e.target.value })}
                    className='w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500'
                    placeholder={t('companies.form.primaryDomainPlaceholder')}
                    required
                  />
                </div>

                <div className='flex items-center'>
                  <input
                    type='checkbox'
                    id='is_active'
                    checked={formData.is_active}
                    onChange={e => setFormData({ ...formData, is_active: e.target.checked })}
                    className='h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded'
                  />
                  <label htmlFor='is_active' className='ml-2 block text-sm text-gray-900 dark:text-gray-300'>
                      {t('companies.form.active')}
                  </label>
                </div>

                <div className='flex justify-end gap-3 pt-4'>
                  <button type='button' onClick={handleCloseModal} className='btn btn-secondary' disabled={submitting}>
                      {t('Cancel')}
                  </button>
                  <button type='submit' className='btn btn-primary' disabled={submitting}>
                    {submitting ? t('Saving...') : (editingCompany ? t('Update') : t('Create'))}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Companies;
